// Rows slide to their new place when a list's order changes (a Case moves up
// as it gets news, an Agent's chat as it answers), instead of jumping there.
//
// FLIP: each row remembers where it was in the last render; when the rows of
// its list come back in another order, every row that moved is drawn back at
// its old place by a transform and eased to zero. The layout is already the
// new one, so only the picture lags behind: nothing reflows, the focus stays
// on the same row (the same element), and a press lands on what is drawn
// under the pointer once the slide ends.
//
// Positions are `offsetTop`, not the screen box: scrolling the list does not
// count as moving, and the scroll position is left as it is. A slide that is
// cut by the next reorder starts the new one from where the row is drawn
// right now, so a row never snaps back first. With reduced motion the rows
// only take their new places.
//
// It needs no ids and no wiring in each screen: `ListRow` takes it, and a row
// that is not a `ListRow` (a sidebar favorite) puts `useListFlip()` on its
// root. A row's list is its parent element (or the list around its `<li>`).
import { type RefCallback, useCallback, useLayoutEffect, useRef } from "react";

const DURATION = 180;
// Fast at first and settling quietly, like a hand putting something down.
const EASING = "cubic-bezier(0.2, 0, 0, 1)";

// Kept on the elements themselves (weakly), so a list needs no owner.
const lastTop = new WeakMap<Element, number>();
const lastOrder = new WeakMap<Element, Element[]>();
const running = new WeakMap<Element, Animation>();

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** The vertical offset a running slide is drawing now (0 without one). */
function drawnOffset(el: HTMLElement): number {
  if (!running.has(el)) return 0;
  const t = getComputedStyle(el).transform;
  const m = /^matrix\((?:[^,]+,){5}\s*([-\d.e]+)\)$/.exec(t ?? "");
  return m ? Number(m[1]) : 0;
}

function slide(el: HTMLElement, dy: number): void {
  const from = dy + drawnOffset(el);
  running.get(el)?.cancel();
  running.delete(el);
  if (from === 0 || typeof el.animate !== "function") return;
  const a = el.animate(
    [{ transform: `translateY(${from}px)` }, { transform: "translateY(0)" }],
    { duration: DURATION, easing: EASING },
  );
  running.set(el, a);
  a.onfinish = () => {
    if (running.get(el) === a) running.delete(el);
  };
}

function sameOrder(a: readonly Element[], b: readonly Element[]): boolean {
  return a.length === b.length && a.every((el, i) => el === b[i]);
}

/**
 * After a render: if the rows of `row`'s list are in another order than last
 * time, slide every row that moved (once per list per render: the first row
 * to look does it for all); otherwise only remember where `row` stands.
 */
function settle(row: HTMLElement): void {
  const parent = row.parentElement;
  const item = parent?.tagName === "LI" ? parent : row;
  const list = item.parentElement;
  if (!list) return;
  const order = Array.from(list.children);
  const before = lastOrder.get(list);
  if (before === undefined || sameOrder(before, order)) {
    if (before === undefined) lastOrder.set(list, order);
    lastTop.set(item, item.offsetTop);
    return;
  }
  lastOrder.set(list, order);
  const move = !reducedMotion();
  for (const el of order) {
    if (!(el instanceof HTMLElement)) continue;
    const was = lastTop.get(el);
    const now = el.offsetTop;
    lastTop.set(el, now);
    if (move && was !== undefined && was !== now) slide(el, was - now);
  }
}

/** Put on a list row's root: the row slides when its list's order changes. */
export function useListFlip<T extends HTMLElement>(): RefCallback<T> {
  const node = useRef<T | null>(null);
  useLayoutEffect(() => {
    if (node.current) settle(node.current);
  });
  return useCallback((el: T | null) => {
    node.current = el;
  }, []);
}
